/**
 * Pure unit conversion — the Energistics ABCD model, no I/O, no React.
 *
 * Every unit carries coefficients (A, B, C, D) that map a value `x` *in that
 * unit* to the dimension's SI base:
 *
 *     base = (A + B·x) / (C + D·x)
 *
 * To render a value stored in one unit into another unit of the same dimension
 * we go through the base:  x_stored → base → x_display. The base→unit step is
 * the algebraic inverse of the formula above.
 *
 * This is the only place engineering-style unit math lives on the frontend, and
 * it is *display* conversion only (allowed by the charter). It never mutates a
 * stored value — callers pass values in, get display values out.
 */
import type { ResolvedUnit } from "./types";

/** Convert a value expressed in `unit` to the dimension's SI base. */
export function toBase(x: number, unit: ResolvedUnit): number {
  const { conv_a: a, conv_b: b, conv_c: c, conv_d: d } = unit;
  return (a + b * x) / (c + d * x);
}

/**
 * Convert a value from the dimension's SI base into `unit` — the algebraic
 * inverse of {@link toBase}. From  base = (A + B·y)/(C + D·y)  solve for y:
 *
 *     y = (A − C·base) / (D·base − B)
 *
 * For the common affine unit (D = 0) this reduces to  y = (base·C − A) / B.
 */
export function fromBase(base: number, unit: ResolvedUnit): number {
  const { conv_a: a, conv_b: b, conv_c: c, conv_d: d } = unit;
  if (d === 0) {
    // Affine (the overwhelmingly common case: ft, psi, °F, ppg, …). B is never 0
    // for a real unit, but guard so a malformed row can't divide-by-zero.
    return b === 0 ? base : (base * c - a) / b;
  }
  const denom = d * base - b;
  return denom === 0 ? base : (a - c * base) / denom;
}

/**
 * Convert `value` from the unit `from` to the unit `to` (same dimension),
 * routing through the SI base. When `from` and `to` are the identical unit the
 * result is `value` unchanged (no round-trip rounding). Null passes through.
 */
export function convert(
  value: number | null | undefined,
  from: ResolvedUnit,
  to: ResolvedUnit,
): number | null {
  if (value == null || !Number.isFinite(value)) return null;
  if (from.unit_key === to.unit_key) return value;
  return fromBase(toBase(value, from), to);
}

/** Apply a scalar FX rate to a money amount (base→quote). Null passes through. */
export function applyFx(amount: number | null | undefined, rate: number): number | null {
  if (amount == null || !Number.isFinite(amount)) return null;
  return amount * rate;
}
